import { useMemo } from 'react';

import type { NearbyCoach } from '@/features/onboarding/components/onboarding-step-find-academy';
import {
  COACH_DIRECTORY_CATEGORIES,
  COACH_DIRECTORY_ORG_SLUG,
  searchCoachDirectory,
  type CoachDirectoryResult,
} from '@/features/onboarding/services/coach-directory';
import { useInfiniteQuery } from '@tanstack/react-query';

type Center = { latitude: number; longitude: number } | null | undefined;

const DEFAULT_PAGE_SIZE = 20;

/** Maps a directory result into the step's `NearbyCoach` view model. */
function toNearbyCoach(item: CoachDirectoryResult): NearbyCoach | null {
  const coach = item.coach;
  if (!coach?.id || item.lat == null || item.lng == null) return null;
  return {
    id: coach.id,
    // `name` is the coach's personal name; `displayName` is a certification title
    // (e.g. "Pathway Certified Indoor"), so prefer `name` for the row label.
    name: (coach.name || coach.displayName || '').trim(),
    academy: (item.facilityName || '').trim(),
    address: item.address?.trim() || undefined,
    verified: (coach.badges?.length ?? 0) > 0,
    coordinate: { latitude: item.lat, longitude: item.lng },
    thumb: coach.imageUrl ? { uri: coach.imageUrl } : undefined,
    awards: (coach.awards ?? []).map(a => a.imageUrl).filter((url): url is string => Boolean(url)),
    badges: (coach.badges ?? []).map(b => b.badgeUrl).filter((url): url is string => Boolean(url)),
    category: coach.category,
  };
}

/**
 * Fetches real coaches from the booking-engine directory around `center`, mapped to the
 * step's `NearbyCoach` shape. Paginated (`page`/`pageSize`) via `useInfiniteQuery` so the
 * list can load more as the guardian scrolls — call `fetchNextPage` on scroll-end. Gated
 * on `center` so it only fires once the guardian's location is known.
 */
export function useNearbyCoaches(center: Center, radiusMiles: number) {
  const query = useInfiniteQuery({
    queryKey: ['nearby-coaches', center?.latitude ?? null, center?.longitude ?? null, radiusMiles],
    enabled: center != null,
    initialPageParam: 1,
    queryFn: ({ pageParam }) =>
      searchCoachDirectory({
        orgSlug: COACH_DIRECTORY_ORG_SLUG,
        coachCategory: COACH_DIRECTORY_CATEGORIES,
        includeFilters: false,
        page: pageParam,
        pageSize: DEFAULT_PAGE_SIZE,
        mode: 'radius',
        center: { lat: center!.latitude, lng: center!.longitude },
        radiusMiles,
        sortBy: 'distance',
      }),
    getNextPageParam: (lastPage, allPages) => {
      const loaded = allPages.reduce((sum, page) => sum + (page.results?.length ?? 0), 0);
      const total = lastPage.total ?? 0;
      // Next page only while we haven't loaded everything and the last page was full.
      return loaded < total && (lastPage.results?.length ?? 0) > 0
        ? allPages.length + 1
        : undefined;
    },
  });

  const coaches = useMemo<NearbyCoach[]>(() => {
    // Pages can overlap (a coach appears on more than one page), so dedupe by id —
    // otherwise the list renders duplicate React keys.
    const seen = new Set<string>();
    const result: NearbyCoach[] = [];
    for (const item of (query.data?.pages ?? []).flatMap(page => page.results ?? [])) {
      const coach = toNearbyCoach(item);
      if (!coach || seen.has(coach.id)) continue;
      seen.add(coach.id);
      result.push(coach);
    }
    return result;
  }, [query.data]);

  return {
    coaches,
    isLoading: query.isLoading,
    isError: query.isError,
    fetchNextPage: query.fetchNextPage,
    hasNextPage: query.hasNextPage,
    isFetchingNextPage: query.isFetchingNextPage,
  };
}
